import { useEffect, useRef, useState } from 'preact/hooks';
import { Link, useParams } from 'wouter-preact';
import { ArrowLeft } from 'lucide-preact';
import { api } from '@/lib/teaching/teacher-api';
import { errText } from '@/lib/teaching/slice2';
import { TREND_WORD, dateOf, type Point, type StudentProgress as SP } from '@/lib/teaching/slice4';
import { TeachingPage, Card, INK, perfFill, pct, teachHref, CodeTag } from '@/components/teaching/ui';
import { Btn, ErrorLine, inputCls, val } from '@/components/teaching/form';
import { TestTag } from './Progress';
import { strandName } from '@/lib/teaching/strands';

// Student progress over time (slice 4 §03, §02, U4, U7, U12). Only
// teacher-confirmed levels are plotted; gaps stay gaps with their reason;
// trend words only between consecutive comparable confirmed levels.

export function StudentTabs({ studentId, a, active }: { studentId: string; a: string | null; active: 'test' | 'progress' }) {
  const tab = (on: boolean) => 'px-3 py-1 rounded-lg text-[12.5px] font-medium ' + (on ? 'bg-[var(--color-accent)]' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)]');
  return (
    <div role="tablist" aria-label="Student view" class="inline-flex rounded-xl border border-[var(--color-border)] p-1 bg-[var(--color-card)]">
      {a ? <Link role="tab" aria-selected={active === 'test'} href={`/teaching/a/${a}/student/${studentId}?a=${a}`} class={tab(active === 'test')} style={active === 'test' ? { color: '#ffffff' } : undefined}>This test</Link>
        : <span class={tab(false) + ' opacity-50'}>This test</span>}
      <Link role="tab" aria-selected={active === 'progress'} href={`/teaching/student/${studentId}/progress${a ? `?a=${a}` : ''}`} class={tab(active === 'progress')} style={active === 'progress' ? { color: '#ffffff' } : undefined}>Progress</Link>
    </div>
  );
}

const MIN_W = 150, LEFT = 40, H = 190, TOP = 18, BOT = 146; // level 8 at TOP, level 0 at BOT
const yOf = (level: number) => BOT - (level / 8) * (BOT - TOP);

/** Column width so the chart fills its card (never under MIN_W per test). */
function useColumnWidth(n: number): [preact.RefObject<HTMLDivElement>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el); setW(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, Math.max(MIN_W, Math.floor((w - LEFT) / Math.max(1, n)))];
}

function LevelLine({ points, W }: { points: Point[]; W: number }) {
  const width = LEFT + points.length * W;
  const x = (i: number) => LEFT + W / 2 + i * W;
  const segs: { i: number; dashed: boolean }[] = [];
  for (let i = 1; i < points.length; i++) {
    const p = points[i], q = points[i - 1];
    if (p.status === 'confirmed' && q.status === 'confirmed' && !p.break_before) segs.push({ i, dashed: p.test.provisional || q.test.provisional });
  }
  return (
    <div class="overflow-x-auto">
      <svg viewBox={`0 0 ${width} ${H}`} width={width} height={H} class="block" role="img" aria-label="Confirmed MYP level over time">
        <text transform={`translate(11 ${(TOP + BOT) / 2}) rotate(-90)`} font-size="10" text-anchor="middle" fill="var(--color-text-muted)">MYP level 0-8</text>
        {[0, 2, 4, 6, 8].map((l) => (
          <g key={l}>
            <line x1={LEFT - 6} x2={width} y1={yOf(l)} y2={yOf(l)} stroke="var(--color-border)" stroke-width="1" />
            <text x={LEFT - 10} y={yOf(l) + 3} font-size="10" text-anchor="end" fill="var(--color-text-faint)">{l}</text>
          </g>
        ))}
        {segs.map(({ i, dashed }) => (
          <line key={i} x1={x(i - 1)} y1={yOf(points[i - 1].level!)} x2={x(i)} y2={yOf(points[i].level!)} stroke="var(--color-accent)" stroke-width="2.5" stroke-dasharray={dashed ? '6 5' : undefined} />
        ))}
        {points.map((p, i) => (
          <g key={p.test.id}>
            {p.break_before && (
              <>
                <line x1={x(i) - W / 2} x2={x(i) - W / 2} y1={TOP - 10} y2={BOT + 4} stroke="#9aa0a8" stroke-dasharray="3 3" />
                <text x={x(i) - W / 2 + 3} y={TOP - 4} font-size="9.5" fill="var(--color-text-muted)">{p.break_before}</text>
              </>
            )}
            {p.status === 'confirmed' ? (
              <>
                <circle cx={x(i)} cy={yOf(p.level!)} r="7" fill="var(--color-accent)" stroke={p.test.provisional ? '#ffffff' : 'none'} stroke-dasharray={p.test.provisional ? '2 2' : undefined} />
                <text x={x(i)} y={yOf(p.level!) - 12} font-size="12" font-weight="600" text-anchor="middle" fill="var(--color-text)">{p.level}</text>
              </>
            ) : p.hollow ? (
              <circle cx={x(i)} cy={BOT + 18} r="6" fill="none" stroke="#c46a1b" stroke-width="2" />
            ) : null}
            {p.status !== 'confirmed' && (
              <text x={x(i) + (p.hollow ? 10 : 0)} y={BOT + 22} font-size="10" text-anchor={p.hollow ? 'start' : 'middle'} fill="var(--color-text-muted)">{p.label}</text>
            )}
            <text x={x(i)} y={H - 4} font-size="10" text-anchor="middle" fill="var(--color-text-faint)">{p.class_name_at_sitting}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

function Equivalence({ prev, cur, onDone }: { prev: Point; cur: Point; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!prev.test.fingerprint || !cur.test.fingerprint) return null;
  async function save() {
    setErr(null);
    try { await api('POST', '/api/t/descriptor-equivalences', { fp_a: prev.test.fingerprint, fp_b: cur.test.fingerprint, reason }); setOpen(false); onDone(); }
    catch (e) { setErr(errText(e)); }
  }
  if (!open) return <Btn onClick={() => setOpen(true)}>Record descriptors as equivalent</Btn>;
  return (
    <div class="flex flex-col gap-1.5 w-full max-w-[520px]">
      <div class="text-[12px] text-[var(--color-text-muted)]">The descriptors for {prev.test.title} and {cur.test.title} differ. If they describe the same standard, say why; trend words then return.</div>
      <div class="flex flex-wrap gap-2">
        <input aria-label="Why these descriptors are equivalent" class={inputCls + ' flex-1 min-w-[200px]'} value={reason} onInput={(e) => setReason(val(e))} placeholder="Same bands, wording tidied" />
        <Btn kind="primary" disabled={!reason.trim()} onClick={save}>Save</Btn>
        <Btn onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
      <ErrorLine error={err} />
    </div>
  );
}

function CriterionCard({ c, reload }: { c: SP['criteria'][number]; reload: () => void }) {
  const [ref, W] = useColumnWidth(c.points.length);
  return (
        <Card title={`Criterion ${c.criterion}`} sub="Only MYP levels you confirmed. A gap is not a zero. A dashed line means that test is not final yet (checkpoint 2 not passed).">
          <div data-criterion={c.criterion} ref={ref}>
            <LevelLine points={c.points} W={W} />
            <div class="overflow-x-auto mt-2">
              <table class="border-collapse text-[12px] table-fixed" style={{ marginLeft: `${LEFT}px` }}>
                <tbody>
                  <tr>
                    {c.points.map((p) => (
                      <td key={p.test.id} class="align-top px-1.5 pb-2 text-center" style={{ width: `${W}px`, minWidth: `${W}px`, maxWidth: `${W}px` }} data-point={p.status} data-test={p.test.title}>

                        <div class="font-medium text-[var(--color-text)]">{p.test.title}</div>
                        <div class="text-[11px] text-[var(--color-text-faint)] mb-1">{dateOf(p.test.sat_on)} · Year {p.test.year_level}</div>
                        <div class="flex flex-wrap items-center justify-center gap-1 mb-1"><TestTag t={p.test} /></div>
                        {p.status === 'confirmed'
                          ? <div data-level><b class="text-[var(--color-text)]">Level {p.level}</b> <span class="text-[11px] text-[var(--color-text-faint)]">confirmed</span></div>
                          : <div class="text-[var(--color-text-muted)]" data-gap>{p.label}</div>}
                        {p.trend && <div data-trend class="mt-0.5"><span class="text-[11px] px-1.5 py-0.5 rounded font-medium" style={{ background: p.trend === 'up' ? '#d3f0d9' : p.trend === 'down' ? '#fddcbf' : '#e2e3e6', color: INK }}>{TREND_WORD[p.trend]}</span></div>}
                        {p.break_before && <div class="text-[11px] text-[var(--color-text-muted)] mt-0.5">Break: {p.break_before}</div>}
                        {p.raw && <div class="text-[10.5px] text-[var(--color-text-faint)] mt-0.5">{p.raw}</div>}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            {c.points.map((p, i) => p.break_before === 'Descriptors changed' && i > 0 ? (
              <div key={p.test.id} class="mt-2"><Equivalence prev={c.points[i - 1]} cur={p} onDone={reload} /></div>
            ) : null)}

            <div class="mt-4">
              <div class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)] mb-1">Strand mastery by test</div>
              <div class="overflow-x-auto rounded-lg border border-[var(--color-border)]">
                <table class="border-collapse text-[12px] w-full">
                  <thead>
                    <tr>
                      <th class="sticky left-0 z-10 bg-[var(--color-card)] text-left px-2 py-1.5 font-medium text-[var(--color-text-muted)]">Strand</th>
                      {c.strand_table.tests.map((t) => <th key={t.test.id} class="px-2 py-1.5 font-medium text-[var(--color-text)] text-left min-w-[110px]">{t.test.title}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {c.strand_table.strands.map((st) => (
                      <tr key={st} class="border-t border-[var(--color-border)]">
                        <td class="sticky left-0 z-10 bg-[var(--color-card)] px-2 py-1 font-medium text-[var(--color-text)] min-w-[160px]">{strandName(st)}</td>
                        {c.strand_table.tests.map((t) => {
                          const cell = t.cells[st];
                          const has = cell && cell.rate !== null;
                          return (
                            <td key={t.test.id} class="p-0.5">
                              <div class="h-7 rounded px-1.5 flex items-center tabular-nums text-[11.5px]" title={cell?.note ?? (cell ? `${cell.mastered} of ${cell.n} answers mastered` : 'no evidence')}
                                style={has ? { background: perfFill(cell!.rate), color: INK } : undefined}>
                                {has ? pct(cell!.rate) : ''}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div class="text-[11px] text-[var(--color-text-faint)] mt-1">Blank = not enough evidence (fewer than 2 marked questions in that strand), never a low score.</div>
            </div>
          </div>
        </Card>
  );
}

export function StudentProgress() {
  const { id } = useParams<{ id: string }>();
  const a = new URLSearchParams(location.search).get('a');
  const [v, setV] = useState<SP | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = () => api<SP>('GET', `/api/t/students/${id}/progress`).then(setV).catch((e) => setErr(errText(e)));
  useEffect(() => { setV(null); void load(); }, [id]);
  const lastTest = a ?? v?.tests[v.tests.length - 1]?.id ?? null;

  return (
    <TeachingPage title={v ? v.student.name : 'Student progress'} breadcrumb={v ? `${v.tests.length} test${v.tests.length === 1 ? '' : 's'} · progress over time` : ''}
      actions={<StudentTabs studentId={id} a={lastTest} active="progress" />}>
      <Link href={teachHref('/teaching', { a: lastTest })} class="inline-flex items-center gap-1 text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
        <ArrowLeft size={13} /> Back
      </Link>
      <ErrorLine error={err} />
      {!v && !err && <div class="text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
      {v && v.criteria.length === 0 && <Card><div class="text-[13px] text-[var(--color-text-muted)]">No tests yet for this student. Their progress appears here after their first marked test.</div></Card>}
      {v && v.criteria.map((c) => <CriterionCard key={c.criterion} c={c} reload={() => void load()} />)}

      {v && (
        <div class="grid lg:grid-cols-2 gap-3">
          <Card title="Feedback thread" sub="Each test's next step beside what the following test showed. For you to read; no claim is made that it was acted on.">
            <div class="flex flex-col gap-3">
              {v.feedback_thread.map((f) => (
                <div key={f.test.id} class="text-[12.5px] border-b border-[var(--color-border)] last:border-0 pb-2">
                  <div class="font-medium text-[var(--color-text)]">{f.test.title} <span class="text-[11px] font-normal text-[var(--color-text-faint)]">{dateOf(f.test.sat_on)}</span></div>
                  <div class="text-[var(--color-text-muted)]"><span class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)]">Next step: </span>{f.next_step ?? 'no approved feedback'}</div>
                  {f.following && (
                    <div class="mt-1 pl-3 border-l-2 border-[var(--color-border)]">
                      <div class="text-[11px] text-[var(--color-text-faint)]">Then in {f.following.test.title}</div>
                      <div class="text-[var(--color-text-muted)]"><span class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)]">Strength: </span>{f.following.strength ?? 'no approved feedback'}</div>
                      <div class="text-[var(--color-text-muted)]"><span class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)]">Improve: </span>{f.following.improvement ?? 'no approved feedback'}</div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
          <Card title="Mistakes that came back" sub="The same mistake showed up in an earlier test too">
            {v.recurring_misconceptions.length === 0 ? <div class="text-[13px] text-[var(--color-text-muted)]">None so far. Nothing this student got wrong has come back in a later test.</div> : (
              <div class="flex flex-col gap-2">
                {v.recurring_misconceptions.map((r, i) => (
                  <div key={i} class="text-[12.5px]">
                    <span class="text-[var(--color-text)] font-medium">{r.meaning}</span><CodeTag code={r.code} />
                    <div class="text-[11.5px] text-[var(--color-text-faint)]">In {r.test.title}, {r.text}</div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
      <div class="h-6" />
    </TeachingPage>
  );
}
